export type ActiveWorkSurface = 'smt' | 'smm';
export type ActiveWorkCustodyStatus = 'claimed' | 'available' | 'finalized';
export type ActiveWorkCustodyEventType = 'opened' | 'released' | 'claimed' | 'finalized';

export interface ActiveWorkHandlerContext {
  readonly actorId: string;
  readonly surface: ActiveWorkSurface;
  readonly deviceId: string;
  readonly at: string;
  readonly sessionRef?: string;
}

export interface ActiveWorkCustodyEvent {
  readonly type: ActiveWorkCustodyEventType;
  readonly handler: ActiveWorkHandlerContext;
  readonly custodyRevision: number;
  readonly workRevision: number;
}

export interface ActiveWorkCustodySnapshot {
  readonly status: ActiveWorkCustodyStatus;
  readonly createdBy: ActiveWorkHandlerContext;
  readonly currentHandler: ActiveWorkHandlerContext | null;
  readonly lastHandler: ActiveWorkHandlerContext | null;
  readonly custodyRevision: number;
  readonly history: readonly ActiveWorkCustodyEvent[];
}

const clone = <T>(value: T): T => structuredClone(value);

const assertText = (value: string, code: string): string => {
  if (!value?.trim()) throw new Error(code);
  return value.trim();
};

const normalizeHandler = (handler: ActiveWorkHandlerContext): ActiveWorkHandlerContext => {
  const actorId = assertText(handler.actorId, 'ACTIVE_WORK_ACTOR_REQUIRED');
  const deviceId = assertText(handler.deviceId, 'ACTIVE_WORK_DEVICE_REQUIRED');
  if (handler.surface !== 'smt' && handler.surface !== 'smm') throw new Error('ACTIVE_WORK_SURFACE_INVALID');
  if (!Number.isFinite(Date.parse(handler.at))) throw new Error('ACTIVE_WORK_TIMESTAMP_INVALID');
  const sessionRef = handler.sessionRef?.trim();
  return {
    actorId,
    surface: handler.surface,
    deviceId,
    at: handler.at,
    ...(sessionRef ? { sessionRef } : {}),
  };
};

const assertRevision = (value: number, code: string): void => {
  if (!Number.isSafeInteger(value) || value < 0) throw new Error(code);
};

const assertCustodyRevision = (custody: ActiveWorkCustodySnapshot, expectedCustodyRevision: number): void => {
  assertRevision(expectedCustodyRevision, 'ACTIVE_WORK_EXPECTED_CUSTODY_REVISION_INVALID');
  if (custody.custodyRevision !== expectedCustodyRevision) throw new Error('ACTIVE_WORK_CUSTODY_REVISION_CONFLICT');
};

export const sameActiveWorkHandler = (
  left: ActiveWorkHandlerContext | null,
  right: ActiveWorkHandlerContext,
): boolean => Boolean(left)
  && left!.actorId === right.actorId
  && left!.surface === right.surface
  && left!.deviceId === right.deviceId;

const eventFor = (
  type: ActiveWorkCustodyEventType,
  handler: ActiveWorkHandlerContext,
  custodyRevision: number,
  workRevision: number,
): ActiveWorkCustodyEvent => ({
  type,
  handler: normalizeHandler(handler),
  custodyRevision,
  workRevision,
});

export function createActiveWorkCustody(
  handler: ActiveWorkHandlerContext,
  workRevision: number,
): ActiveWorkCustodySnapshot {
  assertRevision(workRevision, 'ACTIVE_WORK_WORK_REVISION_INVALID');
  const accepted = normalizeHandler(handler);
  return clone({
    status: 'claimed',
    createdBy: accepted,
    currentHandler: accepted,
    lastHandler: null,
    custodyRevision: 1,
    history: [eventFor('opened', accepted, 1, workRevision)],
  });
}

export function assertActiveWorkCurrentHandler(
  custody: ActiveWorkCustodySnapshot,
  handler: ActiveWorkHandlerContext,
): void {
  const accepted = normalizeHandler(handler);
  if (custody.status === 'finalized') throw new Error('ACTIVE_WORK_FINALIZED');
  if (custody.status !== 'claimed' || !sameActiveWorkHandler(custody.currentHandler, accepted)) {
    throw new Error('ACTIVE_WORK_HANDLER_MISMATCH');
  }
}

export function releaseActiveWorkCustody(
  custody: ActiveWorkCustodySnapshot,
  handler: ActiveWorkHandlerContext,
  expectedCustodyRevision: number,
  workRevision: number,
): ActiveWorkCustodySnapshot {
  assertRevision(workRevision, 'ACTIVE_WORK_WORK_REVISION_INVALID');
  assertCustodyRevision(custody, expectedCustodyRevision);
  if (custody.status === 'finalized') throw new Error('ACTIVE_WORK_FINALIZED');
  if (custody.status !== 'claimed') throw new Error('ACTIVE_WORK_NOT_RELEASABLE');
  const accepted = normalizeHandler(handler);
  if (!sameActiveWorkHandler(custody.currentHandler, accepted)) throw new Error('ACTIVE_WORK_HANDLER_MISMATCH');
  const custodyRevision = custody.custodyRevision + 1;
  return clone({
    ...custody,
    status: 'available',
    currentHandler: null,
    lastHandler: accepted,
    custodyRevision,
    history: [...custody.history, eventFor('released', accepted, custodyRevision, workRevision)],
  });
}

export function claimActiveWorkCustody(
  custody: ActiveWorkCustodySnapshot,
  handler: ActiveWorkHandlerContext,
  expectedCustodyRevision: number,
  workRevision: number,
): ActiveWorkCustodySnapshot {
  assertRevision(workRevision, 'ACTIVE_WORK_WORK_REVISION_INVALID');
  assertCustodyRevision(custody, expectedCustodyRevision);
  if (custody.status === 'finalized') throw new Error('ACTIVE_WORK_FINALIZED');
  if (custody.status === 'claimed') throw new Error('ACTIVE_WORK_ALREADY_CLAIMED');
  if (custody.status !== 'available') throw new Error('ACTIVE_WORK_NOT_CLAIMABLE');
  const accepted = normalizeHandler(handler);
  const custodyRevision = custody.custodyRevision + 1;
  return clone({
    ...custody,
    status: 'claimed',
    currentHandler: accepted,
    custodyRevision,
    history: [...custody.history, eventFor('claimed', accepted, custodyRevision, workRevision)],
  });
}

export function finalizeActiveWorkCustody(
  custody: ActiveWorkCustodySnapshot,
  handler: ActiveWorkHandlerContext,
  expectedCustodyRevision: number,
  workRevision: number,
): ActiveWorkCustodySnapshot {
  assertRevision(workRevision, 'ACTIVE_WORK_WORK_REVISION_INVALID');
  assertCustodyRevision(custody, expectedCustodyRevision);
  if (custody.status === 'finalized') throw new Error('ACTIVE_WORK_FINALIZED');
  const accepted = normalizeHandler(handler);
  if (custody.status !== 'claimed' || !sameActiveWorkHandler(custody.currentHandler, accepted)) {
    throw new Error('ACTIVE_WORK_HANDLER_MISMATCH');
  }
  const custodyRevision = custody.custodyRevision + 1;
  return clone({
    ...custody,
    status: 'finalized',
    currentHandler: null,
    lastHandler: accepted,
    custodyRevision,
    history: [...custody.history, eventFor('finalized', accepted, custodyRevision, workRevision)],
  });
}
